/**
 * BulkStatusModal — Modal fuer Status-Wechsel via Bulk-Aktion (ADMIN-05).
 *
 * 2-stufiges Submit:
 *  1. Status-Dropdown waehlen -> "Vorschau" zeigt Karten-Anzahl + Ziel-Status
 *  2. "Bestaetigen" -> store.runBulkAction
 *
 * WARN-BOX (gelb): "Auto-Workflow wird fuer alle Karten gestoppt."
 *
 * CI-Farben: #E9FF57 + #1A1A1A. NIE weisse Schrift auf Gelb.
 */

import { useState } from 'react';
import { AlertTriangle, X } from 'lucide-react';
import { COLUMNS } from '../lib/columns';
import { useLeadBoard } from '../store/useLeadBoard';
import type { BulkActionResponse } from '../services/leadsApi';
import type { LeadStatus } from '../types';

interface Props {
  onClose: () => void;
}

export default function BulkStatusModal({ onClose }: Props) {
  const selectedLeadIds = useLeadBoard((s) => s.selectedLeadIds);
  const runBulkAction = useLeadBoard((s) => s.runBulkAction);

  const [newStatus, setNewStatus] = useState<LeadStatus | ''>('');
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<BulkActionResponse | null>(null);
  const [inputError, setInputError] = useState<string | null>(null);

  const handlePreview = () => {
    if (!newStatus) {
      setInputError('Bitte Status auswaehlen');
      return;
    }
    setInputError(null);
    setConfirmed(true);
  };

  const handleConfirm = async () => {
    if (!newStatus) return;
    setBusy(true);
    try {
      const res = await runBulkAction({
        leadIds: Array.from(selectedLeadIds),
        action: 'change_status',
        payload: { status: newStatus },
      });
      setResult(res);
      if (res.failed.length === 0) {
        setTimeout(() => onClose(), 1500);
      }
    } catch (e: any) {
      setInputError(e?.message ?? 'Aktion fehlgeschlagen');
    } finally {
      setBusy(false);
    }
  };

  const statusLabel = COLUMNS.find((c) => c.status === newStatus)?.label ?? newStatus;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="bg-white rounded-lg max-w-sm w-full mx-4 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <header className="flex items-center justify-between p-4 border-b border-[#DAD9D9]">
          <h2 className="text-base font-semibold text-[#1A1A1A]">Status wechseln</h2>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-[#1A1A1A] rounded p-1"
            aria-label="Schliessen"
          >
            <X className="w-4 h-4" />
          </button>
        </header>

        {/* Body */}
        <div className="p-4 space-y-4">
          {/* Auto-Workflow-Warn-Box */}
          <div className="bg-[#E9FF57]/30 border border-[#E9FF57] text-[#1A1A1A] px-3 py-2 rounded flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
            <p className="text-sm font-medium">
              Auto-Workflow wird fuer alle Karten gestoppt.
            </p>
          </div>

          {!confirmed ? (
            <>
              <p className="text-sm text-gray-600">
                Neuen Status fuer{' '}
                <span className="font-semibold text-[#1A1A1A]">{selectedLeadIds.size}</span>{' '}
                {selectedLeadIds.size === 1 ? 'Karte' : 'Karten'} auswaehlen:
              </p>

              <div>
                <label className="block text-sm font-medium text-[#1A1A1A] mb-1">
                  Ziel-Status
                </label>
                <select
                  value={newStatus}
                  onChange={(e) => {
                    setNewStatus(e.target.value as LeadStatus);
                    setInputError(null);
                  }}
                  disabled={busy}
                  className={
                    'w-full border rounded px-3 py-2 text-sm text-[#1A1A1A] bg-white ' +
                    (inputError ? 'border-red-500' : 'border-[#DAD9D9]')
                  }
                >
                  <option value="">– Status auswaehlen –</option>
                  {COLUMNS.map((col) => (
                    <option key={col.status} value={col.status}>
                      {col.label}
                    </option>
                  ))}
                </select>
                {inputError && (
                  <p className="text-red-600 text-xs mt-1">{inputError}</p>
                )}
              </div>
            </>
          ) : (
            <>
              {/* Vorschau-Stufe */}
              <div className="bg-gray-50 border border-[#DAD9D9] rounded p-3 text-sm">
                <p className="text-gray-600">
                  <span className="font-semibold text-[#1A1A1A]">{selectedLeadIds.size}</span>{' '}
                  {selectedLeadIds.size === 1 ? 'Karte' : 'Karten'} werden nach{' '}
                  <span className="font-semibold text-[#1A1A1A]">"{statusLabel}"</span> verschoben.
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  Auto-Workflow wird fuer alle betroffenen Karten deaktiviert.
                </p>
              </div>

              {inputError && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-sm">
                  {inputError}
                </div>
              )}

              {/* Erfolgs-Banner */}
              {result && result.failed.length === 0 && (
                <div className="bg-[#E9FF57]/30 border border-[#E9FF57] text-[#1A1A1A] px-3 py-2 rounded text-sm font-medium">
                  {result.successful} {result.successful === 1 ? 'Karte' : 'Karten'} verschoben.
                </div>
              )}

              {/* Fehler-Banner */}
              {result && result.failed.length > 0 && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-sm">
                  {result.successful > 0 && <p>{result.successful} erfolgreich.</p>}
                  <p>{result.failed.length} fehlgeschlagen.</p>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <footer className="flex items-center justify-end gap-2 p-4 border-t border-[#DAD9D9]">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="px-4 py-2 text-sm rounded border border-[#DAD9D9] text-[#1A1A1A] hover:bg-gray-50 disabled:opacity-50"
          >
            Abbrechen
          </button>
          {!confirmed ? (
            <button
              type="button"
              onClick={handlePreview}
              disabled={!newStatus}
              className="px-4 py-2 text-sm rounded bg-[#E9FF57] text-[#1A1A1A] font-medium hover:opacity-90 disabled:opacity-50"
            >
              Vorschau
            </button>
          ) : (
            <button
              type="button"
              onClick={handleConfirm}
              disabled={busy}
              className="px-4 py-2 text-sm rounded bg-[#E9FF57] text-[#1A1A1A] font-medium hover:opacity-90 disabled:opacity-50"
            >
              {busy ? 'Wird ausgefuehrt...' : 'Bestaetigen'}
            </button>
          )}
        </footer>
      </div>
    </div>
  );
}
